import { useState, useEffect } from "react";
import { MapPin, Loader2, Star, Users, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import MobileSelect from "@/components/MobileSelect";
import { isSenderAddress, isRecipientAddress } from "@/lib/addressPrefs";

// Botón exclusivo para administradores que abre un modal con la libreta de
// direcciones de CUALQUIER usuario que tenga direcciones guardadas. Permite
// al admin autollenar origen (remitente) o destino (destinatario) desde las
// libretas de los usuarios, además de su propia libreta (AddressBookButton).
//
// mode: "sender" (filtrar direcciones marcadas como remitente/origen)
//       "recipient" (filtrar direcciones marcadas como destinatario/destino)
//
// onPick recibe { name, phone, street, number, colony, city, state, cp, country, references, email, label, type }.
export default function AdminUserAddressBookButton({ onPick, mode = "sender", title = "Libreta de usuarios", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [usersWithAddresses, setUsersWithAddresses] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const isAdmin = user?.role === "admin";

  // Carga la lista de usuarios que tienen al menos una dirección guardada.
  useEffect(() => {
    if (!open || !isAdmin) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const [agg, users] = await Promise.all([
          base44.entities.Address.aggregate({ groupBy: "created_by_id" }).catch(() => ({ rows: [] })),
          base44.entities.User.list("-created_date", 1000).catch(() => [])
        ]);
        const idsWithCount = new Map((agg?.rows || []).map(r => [r.created_by_id, r.count || 0]));
        const list = (users || [])
          .filter(u => idsWithCount.has(u.id))
          .map(u => ({
            id: u.id,
            name: u.full_name || u.email || u.id.slice(-6),
            email: u.email || "",
            count: idsWithCount.get(u.id) || 0
          }));
        if (!cancelled) {
          setUsersWithAddresses(list);
          if (!selectedUserId && list.length) setSelectedUserId(list[0].id);
        }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isAdmin]);

  // Carga las direcciones del usuario seleccionado, filtradas por modo.
  useEffect(() => {
    if (!open || !selectedUserId) { setAddresses([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingAddresses(true);
      try {
        const page = await base44.entities.Address.filter(
          { created_by_id: selectedUserId },
          { sort: "-updated_date", limit: 200 }
        );
        let recs = page?.items || [];
        // Filtra por modo: remitente (origen) o destinatario (destino). Las flags
        // as_sender / as_recipient / as_shipping tienen prioridad sobre el campo
        // type (que por defecto es "destinatario"). Sin fallback: si no hay
        // coincidencias para ese modo, la lista se muestra vacía.
        if (mode === "sender") {
          recs = recs.filter(isSenderAddress);
        } else if (mode === "recipient") {
          recs = recs.filter(isRecipientAddress);
        }
        if (!cancelled) setAddresses(recs);
      } catch {
        if (!cancelled) setAddresses([]);
      } finally {
        if (!cancelled) setLoadingAddresses(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedUserId, mode]);

  // Solo el admin ve este botón. El early return va después de los hooks
  // para respetar las reglas de hooks de React (orden constante por render).
  if (!isAdmin) return null;

  const pick = (a) => { onPick?.(a); setOpen(false); };

  const selectCls = "w-full bg-ink border border-ink-border px-3 py-2 text-sm focus:border-cyan focus:outline-none";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={title} aria-label={title}
        className={`inline-flex items-center justify-center text-warning hover:text-warning transition-colors ${className}`}>
        <Users className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Users className="w-4 h-4 text-warning" /> Libreta de usuarios
              <span className="text-[0.65rem] uppercase font-bold text-warning border border-warning/40 bg-warning/10 px-1.5 py-0.5">Admin</span>
            </DialogTitle>
          </DialogHeader>

          {loadingUsers ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-warning" /></div>
          ) : usersWithAddresses.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-muted">
              Ningún usuario tiene direcciones guardadas en su libreta.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="label block mb-1.5">Usuario</label>
                <MobileSelect className={selectCls} value={selectedUserId} onChange={setSelectedUserId} options={usersWithAddresses.map(u => ({ value: u.id, label: `${u.name}${u.email ? ` · ${u.email}` : ""} (${u.count} dir.)` }))} />
              </div>

              {loadingAddresses ? (
                <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-warning" /></div>
              ) : addresses.length === 0 ? (
                <div className="py-6 text-center text-sm text-ink-muted">
                  Este usuario no tiene direcciones {mode === "sender" ? "de remitente" : "de destinatario"}.
                </div>
              ) : (
                <div className="grid gap-2 max-h-[50vh] overflow-y-auto pr-1">
                  {addresses.map(a => (
                    <button key={a.id} type="button" onClick={() => pick(a)}
                      className="surface-inset p-3 text-left hover:border-cyan transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-cyan shrink-0" />
                        <span className="font-display font-bold text-sm">{a.label || a.name || "Dirección"}</span>
                        {a.is_default && <Star className="w-3 h-3 text-cyan fill-cyan" />}
                        <div className="ml-auto flex flex-wrap gap-1 justify-end">
                          <span className="text-[0.6rem] uppercase tracking-wider text-ink-muted px-1.5 py-0.5 border border-ink-border">{a.type}</span>
                          {a.as_sender && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Remitente</span>}
                          {a.as_recipient && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Destinatario</span>}
                          {a.as_shipping && <span className="text-[0.6rem] uppercase tracking-wider text-cyan px-1.5 py-0.5 border border-cyan">Envío</span>}
                        </div>
                      </div>
                      <div className="text-xs text-ink-muted leading-relaxed">
                        {a.name && a.name !== a.label && <div className="text-ink-text font-medium">{a.name}</div>}
                        {[a.street, a.number, a.colony, a.city, a.state].filter(Boolean).join(", ")}
                        {a.cp ? ` · C.P. ${a.cp}` : ""}
                        {a.phone ? ` · ${a.phone}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}